import './App.css'
import { motion } from 'framer-motion'
import Navbar from './components/Navbar'
import Hero from './components/Hero'
import AboutMe from './components/AboutMe'
import Skills from './components/Skills'
import Projects from './components/Projects'
import Education from './components/Education'
import Contact from './components/Contact'
import ScrollToTop from './components/ScrollToTop'
import IntroManager from './components/IntroManager'
import useScrollReveal from './hooks/useScrollReveal'
import useScrollBlur from './hooks/useScrollBlur'
import { ThemeProvider } from './context/ThemeContext'

function App() {
  useScrollReveal()
  useScrollBlur()

  return (
    <ThemeProvider>
      <IntroManager>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, ease: 'easeInOut' }}
          className="min-h-screen bg-slate-50 text-slate-800 transition-colors dark:bg-slate-950 dark:text-slate-200"
        >
          <Navbar />
          <main className="pt-16">
            <Hero />
            <AboutMe />
            <Skills />
            <Projects />
            <Education />
            <Contact />
          </main>

          <footer className="relative mt-6 overflow-hidden">
            {/* Curved SVG divider for a modern website footer look */}
            <div className="absolute -top-6 left-0 w-full overflow-hidden leading-none pointer-events-none">
              <svg
                viewBox="0 0 1200 120"
                preserveAspectRatio="none"
                className="w-full h-12 text-white dark:text-slate-950"
                aria-hidden="true"
              >
                <path
                  d="M0,0 C150,100 350,100 600,50 C850,0 1050,0 1200,20 L1200,120 L0,120 Z"
                  fill="currentColor"
                />
              </svg>
            </div>

            <div className="border-t border-slate-200/70 bg-white/90 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-950/95">
              <div className="mx-auto max-w-6xl px-4">
                <div className="grid grid-cols-1 items-center gap-4 lg:grid-cols-3">
                  <p className="text-center text-sm text-slate-600 dark:text-slate-400 lg:text-left">
                    © {new Date().getFullYear()} Omkar Kardel. All rights reserved.
                  </p>

                  <div className="flex items-center justify-center gap-3">
                    <a
                      href="https://github.com/omkarkardel"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="GitHub"
                      className="btn-hover inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-900 text-white shadow-sm dark:bg-white dark:text-slate-900"
                    >
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                        <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.395-.135-.345-.72-1.395-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
                      </svg>
                    </a>

                    <a
                      href="https://www.linkedin.com/in/omkar-kardel-96b855279"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="LinkedIn"
                      className="btn-hover inline-flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-white shadow-sm dark:bg-blue-400 dark:text-slate-900"
                    >
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                        <path d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1 4.98 2.12 4.98 3.5ZM.5 8h4v16h-4V8Zm7 0h3.8v2.2h.1c.53-1 1.83-2.1 3.76-2.1 4.02 0 4.76 2.65 4.76 6.1V24h-4v-8.1c0-1.93-.04-4.42-2.69-4.42-2.7 0-3.11 2.1-3.11 4.28V24h-4V8Z" />
                      </svg>
                    </a>

                    <a
                      href="https://www.instagram.com/omkarkardel171"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Instagram"
                      className="btn-hover inline-flex h-10 w-10 items-center justify-center rounded-full bg-pink-600 text-white shadow-sm dark:bg-pink-400 dark:text-slate-900"
                    >
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37Z" />
                        <path d="M17.5 6.5h.01" />
                      </svg>
                    </a>


                  </div>
                </div>
              </div>
            </div>
          </footer>

          <div className="min-h-6" />
          <ScrollToTop />
        </motion.div>
      </IntroManager>
    </ThemeProvider>
  )
}

export default App
